import { useTranslation } from "react-i18next";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import Box from "@mui/material/Box";

import Button from "features/components/Button";
import IconButton from "features/components/IconButton";

import ShareThreadAction from "./components/threads/ShareThreadAction";
import ThreadList from "./components/threads/ThreadList";
import { useDuck } from "./contexts/DuckContext";
import { useChatContext } from "./contexts/useChatContext";
import { useDuckFullscreen } from "./hooks/useDuckFullscreen";

interface ChatHeaderProps {
  hideRightControls?: boolean;
}

const ChatHeader = ({ hideRightControls }: ChatHeaderProps) => {
  const { setOpen } = useDuck();
  const { isFullscreen, setFullscreen } = useDuckFullscreen();
  const { t } = useTranslation("duck");

  const { messages, isStreaming, createNewThread, focusInput } =
    useChatContext();

  const handleNewThread = async () => {
    await createNewThread();
    focusInput();
  };

  const handleFullscreen = () => {
    if (isFullscreen) {
      setOpen(true);
      setFullscreen(false);

      return;
    }

    setFullscreen(true);
    if (window.heap) {
      window.heap.track("Duck Fullscreen Opened");
    }
  };

  const onCloseClick = () => {
    setFullscreen(false);
    setOpen(false);
  };

  const FullScreenIcon = isFullscreen ? UnfoldLessIcon : UnfoldMoreIcon;

  return (
    <div className="w-full">
      <Box className="w-full flex justify-between items-center px-4 h-12 text-black border-b border-purple-200">
        <Box className="flex items-center gap-2">
          <Button
            onClick={handleNewThread}
            title={t("new_chat")}
            color="secondary"
            size="small"
            disabled={isStreaming || messages.length === 0}
            data-testid="duck-new-thread-cta"
            startIcon={<AddCommentOutlinedIcon />}
          >
            <span className="ml-2">{t("new_chat")}</span>
          </Button>
          <ThreadList />
        </Box>
        <Box className="flex-none flex justify-end items-center gap-2">
          <ShareThreadAction />
          {!hideRightControls && (
            <>
              <IconButton
                size="small"
                color="secondary"
                onClick={handleFullscreen}
                data-testid="duck-fullscreen-cta"
                data-state={isFullscreen ? "expanded" : "collapsed"}
                tooltip={isFullscreen ? t("collapse") : t("expand")}
              >
                <FullScreenIcon className="rotate-45" />
              </IconButton>

              <IconButton
                size="small"
                color="secondary"
                onClick={onCloseClick}
                tooltip={t("close_duck")}
                data-testid="duck-close-cta"
              >
                <CloseOutlinedIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default ChatHeader;
